import { useState } from "react";
import { Link } from "react-router-dom";
import AuthLayout from "@/pages/auth/components/AuthLayout";

const RESET_EMAIL_FN = "https://ukfoujowiwsrrmajzajy.supabase.co/functions/v1/send-reset-email";

export default function ForgotPasswordPage() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    if (!email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError("Please enter a valid email address.");
      return;
    }

    setLoading(true);

    try {
      const res = await fetch(RESET_EMAIL_FN, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim() }),
      });

      // Always show success regardless of whether email exists (security best practice)
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        // Only surface a real server error, not "email not found"
        if (res.status >= 500) {
          setError("Something went wrong on our end. Please try again.");
          setLoading(false);
          return;
        }
      }
    } catch {
      setError("Network error — please check your connection and try again.");
      setLoading(false);
      return;
    }

    setLoading(false);
    setSent(true);
  };

  return (
    <AuthLayout
      title="Reset Your Password"
      subtitle="Enter your email and we'll send you a reset link."
    >
      {sent ? (
        <div className="space-y-6 text-center py-2">
          {/* Success icon */}
          <div className="w-16 h-16 bg-[#f0fdf4] rounded-full flex items-center justify-center mx-auto">
            <i className="ri-mail-check-line text-[#16a34a] text-3xl"></i>
          </div>

          <div>
            <h3 className="text-lg font-bold text-gray-900 mb-2">Check your inbox</h3>
            <p className="text-gray-500 text-sm leading-relaxed">
              We sent a password reset link to{" "}
              <span className="font-semibold text-gray-800">{email}</span>.
              It expires in 1 hour.
            </p>
          </div>

          <div className="bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-xs text-amber-700 text-left flex items-start gap-2">
            <i className="ri-information-line flex-shrink-0 mt-0.5"></i>
            <span>
              Didn&apos;t get it? Check your spam folder, or{" "}
              <button
                onClick={() => setSent(false)}
                className="font-semibold underline cursor-pointer"
              >
                try again
              </button>
              .
            </span>
          </div>

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Back to sign in
          </Link>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg flex items-center gap-2">
              <i className="ri-error-warning-line flex-shrink-0"></i>
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              Email Address
            </label>
            <input
              type="email"
              name="email"
              value={email}
              onChange={(e) => { setEmail(e.target.value); setError(""); }}
              placeholder="john@sunpowersolutions.com"
              autoFocus
              className={`w-full px-4 py-2.5 rounded-lg border text-sm focus:outline-none focus:ring-2 focus:ring-green-500 transition ${
                error ? "border-red-400 bg-red-50" : "border-gray-200 bg-gray-50"
              }`}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-[#16a34a] hover:bg-[#15803d] disabled:opacity-60 text-white font-semibold py-3 rounded-lg text-sm transition-all whitespace-nowrap cursor-pointer"
          >
            {loading ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Sending reset link...
              </span>
            ) : (
              <>
                <i className="ri-mail-send-line mr-2"></i>
                Send Reset Link
              </>
            )}
          </button>

          <Link
            to="/login"
            className="flex items-center justify-center gap-2 text-sm text-gray-500 hover:text-gray-800 transition-colors"
          >
            <i className="ri-arrow-left-line text-xs"></i>
            Back to sign in
          </Link>
        </form>
      )}
    </AuthLayout>
  );
}